/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { IMarker } from 'src/interfaces/Marker';
import classNames from 'classnames';
import { forwardRef, MutableRefObject, useMemo, useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { MARKER_ACTION, MARKER_STATUS, MARKER_TYPE } from 'src/interfaces/enums';
import useApplicationData from 'src/hooks/useApplicationData';
import { formatMediaTime } from 'src/utils/playerUtils';
import { formatDateDistanceByLocale } from 'src/utils/dateUtils/formatDateDistanceByLocale';
import NotesMenu from '../NotesMenu';
import { LoadIcon } from '../../icons';
import NotesErrorButton from '../NotesErrorButton';
import NotesButtonReloadItem from '../NotesButtonReloadItem';
import useNotesContainer from '../../containers/NotesContainer/hooks/useNotesContainer';
import { TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION } from '../../containers/NotesContainer/constants';
import styles from './styles.module.scss';

export interface INotesListItemProps {
  note: IMarker;
  locale?: string;
  onTimeSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRetry: () => void;
}
const NotesListItem = forwardRef<HTMLDivElement, INotesListItemProps>(
  ({ note, locale, onEdit, onDelete, onRetry, onTimeSelect }, ref) => {
    const { t } = useTranslation('common');
    const { canEditMarkers: isProducer } = useApplicationData();
    const [reloadButtonVisible, setReloadButtonVisible] = useState(false);
    const { getPendingNoteById } = useNotesContainer();
    const pendingNote = getPendingNoteById(note.id);

    const containerRef = useRef<HTMLDivElement | null>(null);

    const showWarningNoteIsFromProducer = note.type === MARKER_TYPE.MARK && !isProducer;

    const timeFromVideo = formatMediaTime(Math.round(note.time));

    const dateFormatted = useMemo(() => {
      const dateFormattedByLocale = formatDateDistanceByLocale({
        dateTextWithTimezone: note.updateDate,
        locale,
      });

      return dateFormattedByLocale ? dateFormattedByLocale : t('date_unavailable');
    }, [note.updateDate, locale, t]);

    useEffect(() => {
      const refElement = (ref as MutableRefObject<HTMLDivElement | null>).current;
      if (refElement) {
        containerRef.current = refElement;
      }
    }, [ref]);

    useEffect(() => {
      if (pendingNote?.status !== MARKER_STATUS.PENDING) {
        return;
      }

      const timeToScrollItemToBeVisibleInParentContent = setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION);

      return () => {
        clearTimeout(timeToScrollItemToBeVisibleInParentContent);
      };
    }, [pendingNote]);

    const styleStatus = styles[String(pendingNote?.status).toLocaleLowerCase()];
    return (
      <div
        ref={ref}
        className={classNames(styles.container, styleStatus, styles.noteItems)}
        onKeyDown={(e) => e.key === 'Enter' && onTimeSelect()}
        tabIndex={0}
        role='listitem'
        aria-label={t('aria_labels.notes.comment_item')}
        aria-describedby={`noteItem${note.id}`}
      >
        <div id={`noteItem${note.id}`} className={styles.noteItems}>
          <div className={styles.noteVideoTime}>
            <button
              className={styles.buttonTime}
              type='button'
              onClick={onTimeSelect}
              onMouseDown={(event) => event.preventDefault()}
              aria-label={`${t('aria_labels.notes.go_to_video_time')}: ${timeFromVideo}`}
            >
              <time>{timeFromVideo}</time>
            </button>
            {showWarningNoteIsFromProducer && (
              <strong className={styles.producerNoteInfo}>{t('notes.producer_note')}</strong>
            )}
          </div>
          <div className={styles.noteText}>{note.text}</div>
          <div className={styles.noteDate}>
            <time className={styles.noteUpdateDate} dateTime={note.updateDate}>
              {dateFormatted}
            </time>
          </div>
          {!showWarningNoteIsFromProducer && (
            <NotesMenu
              disabled={pendingNote?.status === MARKER_STATUS.PENDING || pendingNote?.status === MARKER_STATUS.ERROR}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          )}
          <div className={styles.noteFooter}>
            <footer className={styleStatus} data-i={styleStatus}>
              {pendingNote?.status === MARKER_STATUS.PENDING && (
                <span className={classNames(styles.iconContainer, styles.loadingContainer)}>
                  <LoadIcon />
                </span>
              )}
              {pendingNote?.status === MARKER_STATUS.ERROR && (
                <span className={styles.errorIconContainer}>
                  <NotesErrorButton
                    tooltipText={
                      pendingNote?.action === MARKER_ACTION.DELETE
                        ? t('notes.unable_to_delete')
                        : t('notes.unable_to_save')
                    }
                    onClick={() => {
                      setReloadButtonVisible(true);
                    }}
                  />
                </span>
              )}
            </footer>
            {reloadButtonVisible && (
              <div className={styles.containerButtonReload}>
                <NotesButtonReloadItem
                  onClick={() => {
                    setReloadButtonVisible(false);
                    onRetry();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default NotesListItem;
